import React, { useEffect, useState } from "react";
import {
  Button, Card, Collapse, Form, Space, Spin, TagInput, Toast, Typography,
  Popconfirm, Input, InputNumber
} from "@douyinfe/semi-ui";
import { IconPlus, IconDelete } from "@douyinfe/semi-icons";
import { ApiClient } from "../../api/ApiClient";

const apiClient = ApiClient.getDefault();

/** 关键词子分组 */
interface KeywordGroup {
  name: string;
  keywords: string[];
  regex?: string[];
  scorePercent: number;
}

/** 8 大维度规则接口 */
interface NlpCategoryRule {
  category: string;
  categoryName: string;
  weight: number;
  baselinePercent: number;
  positiveKeywords: string[];
  negativeKeywords: string[];
  positiveRegex: string[];
  negativeRegex: string[];
  positiveScore: number;
  negativeScore: number;
  positiveRegexScore: number;
  negativeRegexScore: number;
  positiveGroups?: KeywordGroup[];
  negativeGroups?: KeywordGroup[];
}

/** 关键词词库配置接口 */
interface KeywordConfig {
  categoryRules?: NlpCategoryRule[];
}

const defaultConfig: KeywordConfig = {
  categoryRules: [],
};

/** 维度中文名映射 */
const categoryLabels: Record<string, string> = {
  job: "工作",
  income: "收入",
  providentFund: "公积金",
  house: "有房",
  vehicle: "有车",
  onlineLoan: "网贷",
  creditCard: "信用卡",
  credit: "信用/逾期",
};

export default function ScoringKeywordConfig() {
  const [config, setConfig] = useState<KeywordConfig>(defaultConfig);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.request<KeywordConfig>("/leadScoring/keywords", "GET");
      if (resp.data) {
        setConfig({ ...defaultConfig, ...resp.data });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.request("/leadScoring/keywords", "POST", undefined, config);
      Toast.success("保存成功");
    } catch (e: any) {
      Toast.error(e?.message || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  const updateCategoryRule = (index: number, field: keyof NlpCategoryRule, value: any) => {
    if (!config.categoryRules) return;
    const newRules = [...config.categoryRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setConfig({ ...config, categoryRules: newRules });
  };

  /** 更新某个维度的正面/负面分组 */
  const updateGroups = (
    ruleIndex: number,
    direction: "positiveGroups" | "negativeGroups",
    groupIndex: number,
    field: keyof KeywordGroup,
    value: any
  ) => {
    if (!config.categoryRules) return;
    const rule = config.categoryRules[ruleIndex];
    const groups = [...(rule[direction] || [])];
    groups[groupIndex] = { ...groups[groupIndex], [field]: value };
    updateCategoryRule(ruleIndex, direction, groups);
  };

  /** 添加分组 */
  const addGroup = (ruleIndex: number, direction: "positiveGroups" | "negativeGroups") => {
    if (!config.categoryRules) return;
    const rule = config.categoryRules[ruleIndex];
    const groups = [...(rule[direction] || []), { name: "新分组", keywords: [], regex: [], scorePercent: 10 }];
    updateCategoryRule(ruleIndex, direction, groups);
  };

  /** 删除分组 */
  const removeGroup = (
    ruleIndex: number,
    direction: "positiveGroups" | "negativeGroups",
    groupIndex: number
  ) => {
    if (!config.categoryRules) return;
    const rule = config.categoryRules[ruleIndex];
    const groups = [...(rule[direction] || [])];
    groups.splice(groupIndex, 1);
    updateCategoryRule(ruleIndex, direction, groups);
  };

  const sortKeywords = (index: number, field: "positiveKeywords" | "negativeKeywords") => {
    if (!config.categoryRules) return;
    const rule = config.categoryRules[index];
    const keywords = [...(rule[field] || [])];
    keywords.sort((a, b) => b.length - a.length);
    updateCategoryRule(index, field, keywords);
  };

  const handleResetCategoryRules = async () => {
    try {
      const resp = await apiClient.request<KeywordConfig>(
        "/leadScoring/keywords/resetCategoryRules",
        "POST"
      );
      if (resp.data) {
        setConfig({ ...config, categoryRules: resp.data.categoryRules || [] });
        Toast.success("已重置为默认维度规则");
      }
    } catch (e: any) {
      Toast.error(e?.message || "重置失败");
    }
  };

  /** 渲染分组编辑区域 */
  const renderGroups = (
    ruleIndex: number,
    direction: "positiveGroups" | "negativeGroups",
    label: string
  ) => {
    const rule = config.categoryRules![ruleIndex];
    const groups = rule[direction] || [];
    const hasGroups = groups.length > 0;
    const slot = (rule.weight * 70).toFixed(1);

    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontWeight: 500, fontSize: 13 }}>{label}</span>
          {hasGroups && (
            <span style={{ fontSize: 11, color: "#999" }}>
              槽位 {slot}分 × 百分比 = 实际得分
            </span>
          )}
        </div>

        {hasGroups ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {groups.map((group, gi) => (
              <div key={gi} style={{
                background: "#fafafa", borderRadius: 6, padding: "8px 12px",
                borderLeft: `3px solid ${direction === "positiveGroups" ? "#52c41a" : "#ff4d4f"}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <Input
                    value={group.name}
                    onChange={(v) => updateGroups(ruleIndex, direction, gi, "name", v)}
                    style={{ width: 140, fontSize: 12 }}
                    placeholder="分组名称"
                  />
                  <InputNumber
                    value={group.scorePercent}
                    onChange={(v) => updateGroups(ruleIndex, direction, gi, "scorePercent", v || 10)}
                    min={1}
                    max={100}
                    suffix="%"
                    style={{ width: 90, fontSize: 12 }}
                  />
                  <span style={{ fontSize: 11, color: "#999" }}>
                    = {(rule.weight * 70 * group.scorePercent / 100).toFixed(1)}分
                  </span>
                  <div style={{ flex: 1 }} />
                  <Button
                    size="small"
                    icon={<IconDelete />}
                    theme="borderless"
                    type="danger"
                    onClick={() => removeGroup(ruleIndex, direction, gi)}
                  />
                </div>
                <TagInput
                  value={group.keywords || []}
                  onChange={(v) => updateGroups(ruleIndex, direction, gi, "keywords", v as string[])}
                  placeholder="输入关键词后按回车添加"
                  style={{ width: "100%" }}
                />
                {group.regex && group.regex.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>正则（命中得分组的60%）</div>
                    <TagInput
                      value={group.regex}
                      onChange={(v) => updateGroups(ruleIndex, direction, gi, "regex", v as string[])}
                      placeholder="输入正则后按回车添加"
                      style={{ width: "100%" }}
                    />
                  </div>
                )}
              </div>
            ))}
            <Button
              size="small"
              icon={<IconPlus />}
              onClick={() => addGroup(ruleIndex, direction)}
              style={{ alignSelf: "flex-start" }}
            >
              添加分组
            </Button>
          </div>
        ) : (
          // 无分组时降级显示扁平编辑
          <>
            <TagInput
              value={rule[direction === "positiveGroups" ? "positiveKeywords" : "negativeKeywords"] || []}
              onChange={(v) => updateCategoryRule(
                ruleIndex,
                direction === "positiveGroups" ? "positiveKeywords" : "negativeKeywords",
                v as string[]
              )}
              placeholder="输入关键词后按回车添加"
              style={{ width: "100%" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span style={{ fontSize: 11, color: "#999" }}>
                共 {(rule[direction === "positiveGroups" ? "positiveKeywords" : "negativeKeywords"] || []).length} 个关键词（未分组，命中得满分）
              </span>
              <Button size="small" onClick={() => addGroup(ruleIndex, direction)}>
                转为分组模式
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 1000 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>评分关键词词库配置</h3>
        <Space>
          <Button onClick={handleResetCategoryRules}>重置维度规则</Button>
          <Button theme="solid" loading={saving} onClick={handleSave}>
            保存
          </Button>
        </Space>
      </div>

      {/* ===== 8 大维度规则配置 ===== */}
      {config.categoryRules && config.categoryRules.length > 0 && (
        <Card
          title="NLP 8大维度评分规则"
          style={{ marginBottom: 16 }}
          headerExtraContent={
            <span style={{ fontSize: 12, color: "#999" }}>
              权重合计: {(config.categoryRules.reduce((s, r) => s + r.weight, 0) * 100).toFixed(0)}%
            </span>
          }
        >
          <div style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>
            每个大分类下按"程度"分为多个子分组，排在前面的分组优先匹配，得分 = 权重×70×百分比/100。
            可编辑分组名称、得分百分比和关键词。
          </div>
          <Collapse accordion>
            {config.categoryRules.map((rule, idx) => (
              <Collapse.Panel
                itemKey={rule.category}
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>
                      {rule.categoryName || categoryLabels[rule.category] || rule.category}
                    </span>
                    <span style={{
                      fontSize: 12, background: "#e6f7ff", padding: "2px 8px",
                      borderRadius: 4, color: "#1890ff"
                    }}>
                      {(rule.weight * 100).toFixed(0)}%
                    </span>
                    <span style={{ fontSize: 12, color: "#999" }}>
                      槽位 {(rule.weight * 70).toFixed(1)}分 | 正面分组 {rule.positiveGroups?.length || 0} | 负面分组 {rule.negativeGroups?.length || 0}
                    </span>
                  </div>
                }
              >
                {/* 基准分配置 */}
                <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f0f5ff", borderRadius: 6, display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>基准分</span>
                  <InputNumber
                    value={rule.baselinePercent ?? 40}
                    onChange={(v) => updateCategoryRule(idx, "baselinePercent", v ?? 40)}
                    min={0}
                    max={100}
                    suffix="%"
                    style={{ width: 100 }}
                    size="small"
                  />
                  <span style={{ fontSize: 12, color: "#666" }}>
                    未匹配正/负面时，给槽位 × {(rule.baselinePercent ?? 40)}% = {((rule.weight * 70) * (rule.baselinePercent ?? 40) / 100).toFixed(1)}分
                  </span>
                </div>

                {/* 正面分组 */}
                {renderGroups(idx, "positiveGroups", "正面关键词分组")}

                {/* 负面分组 */}
                {renderGroups(idx, "negativeGroups", "负面关键词分组")}

                {/* 正面正则（只读） */}
                {rule.positiveRegex && rule.positiveRegex.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 13 }}>
                      正面正则表达式（只读，命中得 {(rule.weight * 70 * 0.6).toFixed(1)}分）
                    </div>
                    <div style={{
                      background: "#f6f8fa", padding: 8, borderRadius: 4,
                      fontSize: 12, fontFamily: "monospace", maxHeight: 120, overflow: "auto"
                    }}>
                      {rule.positiveRegex.map((r, i) => (
                        <div key={i} style={{ padding: "2px 0", color: "#52c41a" }}>{r}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 负面正则（只读） */}
                {rule.negativeRegex && rule.negativeRegex.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 13 }}>
                      负面正则表达式（只读，命中得 -{(rule.weight * 70 * 0.3).toFixed(1)}分）
                    </div>
                    <div style={{
                      background: "#f6f8fa", padding: 8, borderRadius: 4,
                      fontSize: 12, fontFamily: "monospace", maxHeight: 120, overflow: "auto"
                    }}>
                      {rule.negativeRegex.map((r, i) => (
                        <div key={i} style={{ padding: "2px 0", color: "#ff4d4f" }}>{r}</div>
                      ))}
                    </div>
                  </div>
                )}
              </Collapse.Panel>
            ))}
          </Collapse>
        </Card>
      )}

      {/* 评分规则说明 */}
      <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 4, fontSize: 13, color: "#666" }}>
        <div><b>评分规则说明：</b></div>
        <div>- 8大维度按子分组匹配，得分 = 权重×70×分组百分比/100</div>
        <div>- 匹配优先级：正面分组 &gt; 正面正则 &gt; 负面分组 &gt; 负面正则</div>
        <div>- 正面命中后忽略负面；分组内排前的关键词优先匹配</div>
        <div>- 正则命中：正面得 60% 槽位分，负面得 -30% 槽位分</div>
        <div>- 规则评分(0-30分) + NLP评分(0-70分) = 综合评分(上限100分)</div>
      </div>
    </div>
  );
}
